// release-ci-gate.ts
//
// Dogfood 2026-10-01 (ME-2026-bdcf7a, sandbox nal/rd/autopilot-dogfood-sandbox):
// the release flow asked for G3 ~4 s after pushing the candidate while the MR
// pipeline was still `running`; G3 was approved, the mission went `released`,
// the pipeline then FAILED. `ciStatus` was computed (review-remote-adapter.ts)
// but only echoed back as `remoteCiStatus` — nothing gated G3 on it, although
// the spec's G3 row requires "required checks pass" first (build spec, G3).
//
// This is the ONE place a CI status becomes a release decision, shared by the
// flow before G3 is asked and by the approved-G3 leg before the first deploy.
// The status is the newest MR pipeline's own GitLab status — GitLab already
// folds `allow_failure` jobs into it, which a jobs-based `some(failed)` did not
// (an allowed-to-fail `semgrep` job read a green pipeline as red).
//
// Only `success` passes. Everything else is one of three answers
// (ReleaseCiNotPassedOutcome): not finished yet, ended badly, or not provable.
// `manual`/`skipped` are NOT waited on: no job is running, so waiting would be a
// wait for nothing — they block with the instruction that clears them (a person
// plays the manual job / fixes `rules`), and the next `run` re-checks either way.
// A status this code does not know blocks too (fail closed, shown clipped).
import { clipUntrustedText } from "./clip-untrusted-text.js";
import type { ReleaseCiNotPassedOutcome } from "./supervisor-release-flow-resume-outcome.js";

export interface CiGateEvidence {
  /** GitLab pipeline status, or the jobs-derived fallback (success/failed/running/unknown). */
  ciStatus: string;
  pipelineUrl?: string;
}

/** Statuses of a pipeline that is queued or running — it can still become a pass. */
const NOT_FINISHED = new Set([
  "created",
  "waiting_for_resource",
  "waiting_for_callback",
  "preparing",
  "pending",
  "running",
  "scheduled",
]);

/** Finished (or finishing) without a pass; `canceling` ends as `canceled`. */
const ENDED_BADLY = new Set(["failed", "canceled", "canceling"]);

const MAX_STATUS_CHARS = 40;
const MAX_URL_CHARS = 200;

/** What the gate holds back: asking for G3 (before it) or the deploy (after it). */
export type CiGateStage = "g3" | "deploy";

function notPassedDetail(status: string, shown: string): string {
  if (status === "manual") {
    return 'pipeline ở trạng thái "manual" — còn job manual chặn pipeline; chạy job manual trong GitLab (hoặc bỏ chặn trong cấu hình CI)';
  }
  if (status === "skipped") {
    return 'pipeline "skipped" — không job nào chạy nên không có bằng chứng CI; kiểm `rules` của CI rồi chạy lại pipeline';
  }
  return `trạng thái pipeline "${status === "" ? "(trống)" : shown}" không phải pass và không nhận ra là đang chạy hay đã hỏng (fail closed)`;
}

/** `undefined` = CI passed. */
export function releaseCiGateOutcome(
  evidence: CiGateEvidence,
  stage: CiGateStage = "g3",
): ReleaseCiNotPassedOutcome | undefined {
  if (evidence.ciStatus === "success") return undefined;
  // Both strings are echoed by the GitLab server (round 2 L5): no control
  // characters, bounded length — the match against the known statuses is done
  // on the raw value first.
  const status = evidence.ciStatus;
  const shownStatus = clipUntrustedText(status, MAX_STATUS_CHARS);
  const where =
    evidence.pipelineUrl === undefined
      ? ""
      : ` (${clipUntrustedText(evidence.pipelineUrl, MAX_URL_CHARS)})`;
  const stop = stage === "deploy" ? "không deploy" : "không xin G3";
  const rerun = "chạy lại `avatar autopilot run`";
  if (NOT_FINISHED.has(status)) {
    return {
      kind: "waiting_ci",
      reason: `Pipeline của MR${where} đang ở trạng thái "${shownStatus}" — CI chưa xong ⇒ ${stop}. Chờ pipeline xong rồi ${rerun}.`,
    };
  }
  if (ENDED_BADLY.has(status)) {
    return {
      kind: "release_ci_failed",
      reason:
        `Pipeline của MR${where} kết thúc "${shownStatus}" ⇒ ${stop}. Xem job lỗi trong pipeline: lỗi hạ tầng/flaky ⇒ ` +
        `retry pipeline trong GitLab rồi ${rerun}; lỗi thật ⇒ sửa (vd \`avatar autopilot redirect --text "<cách sửa>"\`) ` +
        "hoặc bỏ mission bằng `avatar autopilot archive`.",
    };
  }
  return {
    kind: "release_ci_not_passed",
    reason: `Pipeline của MR${where} không được coi là pass: ${notPassedDetail(status, shownStatus)} ⇒ ${stop}. Xử lý xong thì ${rerun}.`,
  };
}

/** How long after the push GitLab may still be creating the MR pipeline (round 2 L6). */
export const PIPELINE_APPEARANCE_GRACE_SEC = 120;

/** A `recorded_at` further ahead than this is not "just now" — see waitingForPipelineToAppear. */
const CLOCK_SKEW_TOLERANCE_MS = 60_000;

/**
 * Round 2 L6: right after the push GitLab has not created the pipeline yet, so
 * "no pipeline for the pushed commit" is `waiting_ci` — but only inside
 * PIPELINE_APPEARANCE_GRACE_SEC of the recorded push (>= closes it). Past it,
 * when the push has no recorded time (an older CLI), or (round 3 L-1) when the
 * recorded time lies in the future beyond ordinary clock skew (60 s; a forged or
 * broken clock must not keep the window open forever), the answer is the old X2
 * one — `pipeline_sha: not_reported` ⇒ `blocked:release_flow` — so a project that
 * runs no MR pipeline at all never waits forever.
 */
export function waitingForPipelineToAppear(input: {
  pushedSha: string;
  recordedAtMs: number | undefined;
  nowMs: number;
}): ReleaseCiNotPassedOutcome | undefined {
  if (input.recordedAtMs === undefined) return undefined;
  const ageMs = input.nowMs - input.recordedAtMs;
  if (ageMs < -CLOCK_SKEW_TOLERANCE_MS || ageMs >= PIPELINE_APPEARANCE_GRACE_SEC * 1000) {
    return undefined;
  }
  const ageSec = Math.max(0, Math.floor(ageMs / 1000));
  return {
    kind: "waiting_ci",
    reason:
      `Chưa thấy pipeline nào của MR cho commit ${input.pushedSha} (push cách đây ${ageSec}s) — GitLab thường tạo ` +
      `pipeline vài giây sau push ⇒ chưa xin G3. Chờ rồi chạy lại \`avatar autopilot run\`; quá ` +
      `${PIPELINE_APPEARANCE_GRACE_SEC}s mà vẫn không có pipeline thì \`blocked:release_flow\` ` +
      "(MR không chạy pipeline: kiểm CI của project).",
  };
}
